import {
  Classes,
  Colors,
  HTMLTable,
  Icon,
  NonIdealState,
} from "@blueprintjs/core";
import classNames from "classnames";
import get from "lodash.get";
import * as React from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

import { useDatatable2Context } from "../DataTable2";
import { FixedColumnSide } from "./IFixedColumns";
import { AggregateResultType, IRowFunctions } from "nsitools-react";
import { IDataTableColumn } from "..";

export interface IDataGridProps {
  fixed?: boolean;
  side?: FixedColumnSide;
  fixedLayout?: boolean;
}

// noinspection CssInvalidPropertyValue
const Container = styled.div`
  /* ===== Scrollbar CSS ===== */
  /* Firefox */
  scrollbar-width: auto;
  scrollbar-color: #c1c1c1 transparent;

  /* Chrome, Edge, and Safari */

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 10px;
    border: 3px solid transparent;
  }

  &.fixed-none {
    flex: 1;
    display: block;
    overflow-x: overlay;
    white-space: nowrap;
  }

  &.fixed-left {
    box-shadow: 5px 0 5px -5px lightgrey;
  }

  &.fixed-right {
    box-shadow: -5px 0 5px -5px lightgrey;
  }

  & .${Classes.NON_IDEAL_STATE} {
    padding-bottom: 10px;
  }
`;

export const StyledHTMLTable = styled(HTMLTable)<{
  customcss?: FlattenSimpleInterpolation;
  fixedLayout?: boolean;
}>`
  width: 100%;
  table-layout: ${(props) => (props.fixedLayout ? "fixed" : "auto")};

  &.full-height {
    height: 100%;
  }

  ${(props) => (props.customcss ? props.customcss : "")}
  position: relative;
`;

export const StyledTR = styled.tr<{ customCss?: FlattenSimpleInterpolation }>`
  ${(props) => (props.customCss ? props.customCss : "")}
`;

export const StyledTD = styled.td<{
  textEllipsisAfterWidth?: string;
  customCss?: FlattenSimpleInterpolation;
}>`
  ${(props) => (props.customCss ? props.customCss : "")}
  ${(props) =>
    props.textEllipsisAfterWidth
      ? css`
          max-width: ${props.textEllipsisAfterWidth};

          & > * {
            width: 100%;
            display: inline-block;
            overflow: clip;
            text-overflow: ellipsis;
          }
        `
      : ""}
`;

export const NoDataContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: row;
`;

const isDate = (date: any) => {
  return (
    date &&
    Object.prototype.toString.call(date) === "[object Date]" &&
    !isNaN(date)
  );
};

export const DataGrid: React.FunctionComponent<IDataGridProps> = ({
  fixed = false,
  side = "none",
  fixedLayout,
}) => {
  const {
    formatDate,
    customizeTableStyle,
    htmlTableOptions,
    interactive,
    onRowClick,
    showHeader = true,
    customizeRowStyle,
    keyFieldName,
    renderSubComponent,
    loading,
    renderNoData,
    translateFunc,
    stickyHeader,
    renderColumnHeader,
    tableState: {
      toggleSort,
      getFieldnameSortInfo,
      page,
      totalCount,
      aggregateResults,
    },
    visibleColumns,
    footerStyle,
  } = useDatatable2Context();

  const finalHtmlTableOptions = React.useMemo(
    () =>
      htmlTableOptions || {
        condensed: true,
        bordered: true,
        interactive: interactive ? interactive : !!onRowClick,
        striped: true,
      },
    [htmlTableOptions, interactive, onRowClick]
  );

  const onColumnHeaderClick = React.useCallback(
    (col: IDataTableColumn<any>) => {
      if (!col.disableSorting) {
        toggleSort(col.fieldName);
      }
    },
    [toggleSort]
  );

  const renderColumnHeaderSort = React.useCallback(
    (col: IDataTableColumn<any>) => {
      if (!col.disableSorting) {
        switch (getFieldnameSortInfo(col.fieldName)) {
          case "ASC":
            return <Icon icon="caret-up" />;
          case "DESC":
            return <Icon icon="caret-down" />;
          default:
            return null;
        }
      }
    },
    [getFieldnameSortInfo]
  );

  const [openedSubComponents, setOpenedSubComponents] = React.useState<
    string[]
  >([]);

  const toggleOpenedSubComponents = React.useCallback(
    (data: any, state?: boolean) => {
      if (!keyFieldName) return;
      const key = data[keyFieldName];
      if (!key) {
        console.warn(
          `The provided key field name '${key}' does not exists in ${JSON.stringify(
            data,
            null,
            2
          )}`
        );
      }
      setOpenedSubComponents((currentOpened) => {
        const forceState =
          state !== undefined ? state : !currentOpened.includes(key);
        if (forceState) {
          return [...currentOpened, key];
        } else {
          return currentOpened.filter((d) => d !== key);
        }
      });
    },
    [keyFieldName]
  );

  const isSubPanelOpened = React.useCallback(
    (item: any) => {
      return openedSubComponents.includes(item[keyFieldName]);
    },
    [keyFieldName, openedSubComponents]
  );

  const getRowFunctions = React.useCallback(
    (item: any) => {
      const rowFunctions: IRowFunctions = {
        toggleSubPanel: (state) => toggleOpenedSubComponents(item, state),
        subPanelOpened: isSubPanelOpened(item),
      };
      return rowFunctions;
    },
    [isSubPanelOpened, toggleOpenedSubComponents]
  );

  const prepareRowClick = React.useCallback(
    (item: any) => {
      if (onRowClick) {
        onRowClick(item, getRowFunctions(item));
      }
    },
    [getRowFunctions, onRowClick]
  );

  const renderItemCell = React.useCallback(
    (col: IDataTableColumn<any>, rowCount: number, item: any) => {
      if (col.render) {
        return col.render(item, rowCount, getRowFunctions(item));
      } else {
        let value = get(item, "" + col.fieldName);
        if (isDate(value) && formatDate) value = formatDate(value);
        else {
          value = value !== undefined && value !== null ? "" + value : "";
        }
        return col.textEllipsisAfterWidth ? (
          <span title={value}>{value}</span>
        ) : (
          value
        );
      }
    },
    [formatDate, getRowFunctions]
  );

  const renderAggregateContent = React.useCallback(
    (col: IDataTableColumn, aggregateResults: AggregateResultType[]) => {
      if (col.renderAggregate) {
        return col.renderAggregate(aggregateResults);
      }

      const aggregateItem = aggregateResults.find(
        (d: any) =>
          d.key.toUpperCase() === ("" + col.fieldName.toString()).toUpperCase()
      );
      const aggregateValue =
        aggregateItem &&
        `${
          aggregateResults.find(
            (d: any) =>
              d.key.toUpperCase() ===
              ("" + col.fieldName.toString()).toUpperCase()
          )?.value
        }`;

      const prefix = aggregateItem && `${aggregateItem.text}`;

      return aggregateValue !== undefined && aggregateValue !== null
        ? `${prefix}: ${aggregateValue}`
        : "";
    },
    []
  );

  const renderNoDataComponent = React.useCallback(
    () => (
      <NoDataContainer>
        {renderNoData ? (
          renderNoData
        ) : (
          <NonIdealState icon="search" title={translateFunc("GeneralNoData")} />
        )}
      </NoDataContainer>
    ),
    [renderNoData, translateFunc]
  );

  const effectiveColumns = React.useMemo(() => {
    const fixedColumns = visibleColumns.filter((c) => !!c?.fixed);
    if (!fixed) {
      return fixedColumns.length === 0
        ? visibleColumns
        : visibleColumns.filter(
            (c) => !fixedColumns.map((c) => c.fieldName).includes(c.fieldName)
          );
    }
    if (!side) {
      console.error(
        "Please provide a side for this DataGrid into nsitools-react !"
      );
    }
    return visibleColumns.filter((c) =>
      fixedColumns
        .filter((c) => c?.fixed === side)
        .map((c) => c.fieldName)
        .includes(c.fieldName)
    );
  }, [fixed, side, visibleColumns]);

  const displayNoData = React.useMemo(
    () => totalCount === 0 && !loading && !fixed,
    [fixed, loading, totalCount]
  );

  const hasFooter = React.useMemo(
    () => visibleColumns.some((d) => d.footer),
    [visibleColumns]
  );

  return (
    <Container className={"fixed-" + side}>
      <StyledHTMLTable
        fixedLayout={fixedLayout}
        className={classNames({ "full-height": !displayNoData })}
        {...finalHtmlTableOptions}
        customcss={customizeTableStyle ? customizeTableStyle() : null}
      >
        {/* HEADER RENDERING */}
        {showHeader && (
          <>
            <thead>
              <tr
                style={{
                  position: stickyHeader ? "sticky" : null,
                  top: stickyHeader ? 0 : null,
                }}
              >
                {effectiveColumns.map((col) => (
                  <th
                    key={"col_" + col.fieldName}
                    style={{
                      cursor: col.disableSorting ? null : "pointer",
                      width: col.autoFitContent
                        ? "1%"
                        : isNaN(+col.width)
                        ? col.width
                        : col.width + "px",
                      minWidth: col.minWidth ?? 0 + "px",
                      whiteSpace: "normal",
                      textAlign: col.headerAlignment || "left",
                      position: col.fixed === "left" ? "sticky" : null,
                      left: col.fixed === "left" ? 0 : null,
                    }}
                    onClick={() => onColumnHeaderClick(col)}
                  >
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "space-between",
                      }}
                    >
                      {renderColumnHeader(col)}
                      {!col.disableSorting && (
                        <>{renderColumnHeaderSort(col)}</>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              {stickyHeader && (
                <tr
                  style={{
                    position: stickyHeader ? "sticky" : null,
                    top: stickyHeader ? 30 : null,
                  }}
                >
                  <th
                    colSpan={effectiveColumns.length}
                    style={{
                      padding: 0,
                      height: 1,
                      boxShadow: `inset 0px -1px 0 ${Colors.LIGHT_GRAY1}`,
                    }}
                  />
                </tr>
              )}
            </thead>
          </>
        )}
        <tbody>
          {/* ROW RENDERING */}
          {page.map((item: any, rowNum) => (
            <React.Fragment key={rowNum}>
              <StyledTR
                customCss={customizeRowStyle ? customizeRowStyle(item) : null}
                style={{ cursor: onRowClick ? "pointer" : "default" }}
                onClick={() => prepareRowClick(item)}
              >
                {effectiveColumns.map((col, i) => (
                  <StyledTD
                    textEllipsisAfterWidth={col.textEllipsisAfterWidth}
                    key={"cell_" + rowNum + "_" + i}
                    customCss={
                      col.customizeCellStyle
                        ? col.customizeCellStyle(item, rowNum)
                        : null
                    }
                    onClick={(e) => {
                      if (col.fieldName === "__selectionKey")
                        e.stopPropagation();
                    }}
                    style={{
                      textAlign: col.alignment,
                      width: col.autoFitContent
                        ? "1%"
                        : isNaN(+col.width)
                        ? col.width
                        : col.width + "px",
                      minWidth: col.minWidth ?? 0 + "px",
                      position: col.fixed === "left" ? "sticky" : null,
                      left: col.fixed === "left" ? 0 : null,
                    }}
                  >
                    {renderItemCell(col, rowNum, item)}
                  </StyledTD>
                ))}
              </StyledTR>

              {/* SUB COMPONENTS */}
              {renderSubComponent && isSubPanelOpened(item) && (
                <>
                  <tr style={{ height: 0, display: "none" }}>
                    <td style={{ height: 0 }} />
                  </tr>
                  <StyledTR>
                    <StyledTD colSpan={effectiveColumns.length}>
                      {renderSubComponent(item)}
                    </StyledTD>
                  </StyledTR>
                </>
              )}
            </React.Fragment>
          ))}
          {aggregateResults.length > 0 && totalCount > 0 && (
            <StyledTR key={page.length + 1}>
              {effectiveColumns.map((col, i) => (
                <StyledTD
                  textEllipsisAfterWidth={col.textEllipsisAfterWidth}
                  key={`cell_${page.length + 1}_${i}`}
                  customCss={
                    col.customizeAggregateStyle
                      ? col.customizeAggregateStyle()
                      : css({
                          background: "lightgrey !important",
                        })
                  }
                  style={{
                    textAlign: col.alignment,
                    width: col.autoFitContent ? "1%" : col.width + "px",
                    minWidth: col.minWidth ?? 0 + "px",
                    position: col.fixed === "left" ? "sticky" : null,
                    left: col.fixed === "left" ? 0 : null,
                  }}
                >
                  {renderAggregateContent(col, aggregateResults)}
                </StyledTD>
              ))}
            </StyledTR>
          )}
        </tbody>
        <tfoot>
          {hasFooter && totalCount > 0 && (
            <tr key={page.length + 1} style={footerStyle}>
              {effectiveColumns.map((col) => (
                <td
                  // textEllipsisAfterWidth={col.textEllipsisAfterWidth}
                  key={`cell_${page.length + 1}_${col.fieldName}`}
                  // customCss={null}
                  style={{
                    textAlign: col.alignment,
                    width: col.autoFitContent ? "1%" : col.width + "px",
                    minWidth: col.minWidth ?? 0 + "px",
                    position: col.fixed === "left" ? "sticky" : null,
                    left: col.fixed === "left" ? 0 : null,
                  }}
                >
                  {col.footer}
                </td>
              ))}
            </tr>
          )}
        </tfoot>
      </StyledHTMLTable>
      {displayNoData && renderNoDataComponent()}
    </Container>
  );
};
